/**
 * Livraison préparée EN CAISSE (12/10/2026).
 *
 * Philippe prépare à la caisse (vendeur « COMMANDE ») le panier d'un client
 * qui ne commande pas en ligne, un client à la fois, puis le livre ; le client
 * paie à la porte (Bancontact ou espèces). « 🚚 À livrer » transforme le panier
 * en commande à livrer : le stock sort à la préparation, la vente se crée à la
 * porte depuis l'app livreur.
 *
 * Règles pures : panier → lignes envoyées au serveur, date proposée, ticket de
 * préparation (il accompagne le colis et tient lieu de ticket client).
 */
import type { CartLine } from '../types/pos.types';
import type { PrintJob, PrintLine } from '../services/printers';
import { vatRateOf, formatQty } from './pos.utils';
import { normalizeForPrint } from '../components/pos/PreorderCreateModal';

/** Forme attendue par `POST /orders/a-livrer` (`LignePanierLivraison` côté API). */
export interface LignePanierLivraison {
  product_id: string | null;
  product_name: string;
  qty: number;
  unit_price_cents: number;
  tax_rate: number;
  is_weighted: boolean;
  is_deposit: boolean;
  lot_id?: string | null;
  lot_promo_percent?: number | null;
}

const auPoids = (p: CartLine['product']) => !!p && (!!p.is_weighted || p.unit === 'kg');

/**
 * Le panier tel qu'il partira en livraison. Un retour, un remboursement ou un
 * bon cadeau n'ont rien à faire dans un colis : la livraison est refusée et la
 * raison est dite, plutôt que de les perdre en silence.
 */
export function lignesLivraisonDepuisPanier(cart: CartLine[]): { lignes: LignePanierLivraison[]; refus: string | null } {
  const intrus = cart.find((l) => l.is_return || l.is_refund || l.is_gift_voucher || l.qty <= 0);
  if (intrus) {
    const nom = intrus.product_name || intrus.product?.name || 'Article';
    return {
      lignes: [],
      refus: `« ${nom} » est un retour, un remboursement ou un bon cadeau : retirez-le du panier avant de préparer une livraison.`,
    };
  }
  if (cart.length === 0) return { lignes: [], refus: 'Le panier est vide.' };
  return {
    refus: null,
    lignes: cart.map((l) => ({
      product_id: l.product?.id || null,
      product_name: l.product_name || l.product?.name || 'Article',
      qty: l.qty,
      unit_price_cents: l.unit_price_cents,
      tax_rate: l.tax_rate ?? (l.product ? vatRateOf(l.product.tax_class) : 0.21),
      is_weighted: auPoids(l.product),
      is_deposit: !!l.is_deposit,
      ...(l.lot_id ? { lot_id: l.lot_id, lot_promo_percent: l.lot_promo_percent ?? null } : {}),
    })),
  };
}

/** Total au centime par ligne, comme la caisse et le serveur. */
export function totalLivraisonCents(lignes: LignePanierLivraison[]): number {
  return lignes.reduce((s, l) => s + Math.round(l.unit_price_cents * l.qty), 0);
}

function jourLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Date proposée : aujourd'hui si c'est un jour de livraison (on prépare puis on
 * part), sinon le prochain jour de livraison. Sans jours réglés : aujourd'hui.
 */
export function jourDeLivraisonPropose(jours: number[] | null | undefined, maintenant: Date = new Date()): string {
  const d = new Date(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate());
  const valides = (jours || []).filter((j) => j >= 0 && j <= 6);
  if (valides.length === 0) return jourLocal(d);
  for (let i = 0; i < 7; i++) {
    if (valides.includes(d.getDay())) return jourLocal(d);
    d.setDate(d.getDate() + 1);
  }
  return jourLocal(d);
}

/**
 * Contenu du QR d'un colis. Le MÊME format est lu par l'app livreur
 * (`packages/driver/src/utils/qrColis.ts`) : un colis scanné chez le mauvais
 * client y déclenche l'écran rouge.
 */
export function contenuQrColis(orderNumber: string, k: number, n: number): string {
  return `FOLIES|${orderNumber}|${k}/${n}`;
}

/** Les 4 derniers chiffres du numéro, imprimés en grand : le contrôle à la main si le QR ne passe pas. */
export function codeCourtColis(orderNumber: string): string {
  const chiffres = orderNumber.replace(/\D/g, '');
  return chiffres.slice(-4).padStart(4, '0');
}

/**
 * Ticket de livraison : UN SEUL, pour le CLIENT (20/10/2026).
 *
 * Il portait le QR du livreur (`FOLIES|<commande>|k/n`) et un ticket par
 * colis. Depuis l'étiquette Zebra unique (19/10), c'est elle qui sert au
 * contrôle à la porte ; le ticket voyage avec la commande et appartient au
 * client (décision de Philippe, 16/10) : le nombre de colis y est une
 * information, et son seul QR mène à SA commande (`lienClient`, page publique
 * signée). Sans lien (réseau coupé), pas de QR plutôt qu'un QR faux.
 */
export function ticketLivraison(data: {
  orderNumber: string;
  storeName: string;
  customerName: string;
  customerPhone?: string | null;
  adresse: string;
  date: string;
  lignes: LignePanierLivraison[];
  notes?: string | null;
  colis?: number;
  /** Commande payée en ligne : rien à encaisser à la porte. */
  dejaPaye?: boolean;
  /** Adresse publique de la commande (QR destiné au client). */
  lienClient?: string | null;
}): PrintJob {
  const lines: PrintLine[] = [];
  const n = Math.max(1, Math.round(data.colis ?? 1));
  const total = totalLivraisonCents(data.lignes);
  lines.push({ type: 'text', content: normalizeForPrint(data.storeName), align: 'center', bold: true });
  lines.push({ type: 'separator' });
  lines.push({ type: 'text', content: 'LIVRAISON', align: 'center', bold: true, fontSize: 'large' });
  lines.push({ type: 'text', content: data.orderNumber, align: 'center' });
  if (n > 1) lines.push({ type: 'text', content: `Votre livraison : ${n} colis`, align: 'center', bold: true });
  lines.push({ type: 'separator' });
  lines.push({ type: 'text', content: normalizeForPrint(data.customerName), bold: true });
  lines.push({ type: 'text', content: normalizeForPrint(data.adresse) });
  if (data.customerPhone) lines.push({ type: 'text', content: `Tel : ${data.customerPhone}` });
  const jour = new Date(data.date + 'T12:00:00').toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' });
  lines.push({ type: 'text', content: normalizeForPrint(`Livraison : ${jour}`) });
  lines.push({ type: 'separator' });
  for (const l of data.lignes) {
    const ligne = Math.round(l.unit_price_cents * l.qty);
    const qte = l.is_weighted ? `${formatQty(l.qty)} kg` : `${formatQty(l.qty)}`;
    lines.push({ type: 'text', content: normalizeForPrint(l.product_name) });
    lines.push({ type: 'text', content: `  ${qte} x ${(l.unit_price_cents / 100).toFixed(2)} = ${(ligne / 100).toFixed(2)} EUR` });
  }
  lines.push({ type: 'separator' });
  if (data.dejaPaye) {
    lines.push({ type: 'text', content: 'DEJA PAYE - rien a encaisser', bold: true, fontSize: 'large', align: 'center' });
  } else {
    lines.push({ type: 'text', content: `A PAYER : ${(total / 100).toFixed(2)} EUR`, bold: true, fontSize: 'large' });
    lines.push({ type: 'text', content: 'Bancontact ou especes a la livraison', align: 'center' });
  }
  if (data.notes) {
    lines.push({ type: 'separator' });
    lines.push({ type: 'text', content: normalizeForPrint(`Note : ${data.notes}`) });
  }
  if (data.lienClient) {
    lines.push({ type: 'separator' });
    lines.push({ type: 'text', content: 'Votre commande en ligne :', align: 'center' });
    lines.push({ type: 'qrcode', content: data.lienClient, align: 'center', size: 6 });
  }
  lines.push({ type: 'feed', feeds: 3 });
  lines.push({ type: 'cut' });
  return { lines, metadata: { saleNumber: data.orderNumber, timestamp: new Date() } };
}
